<template>
  <VModal name="select-plan-modal" :loading="!selectedPlan || loading">
    <template v-slot:header>
      <h2 class="o-modal__box__header">{{ $t('companySettings.billing.confirmPlan.title') }}</h2>
    </template>
    <template v-slot:content>
      <VForm v-if="currentPlan" :onSubmit="switchPlan">
        <div>{{ isDowngradeToPAYG }}</div>
        <div
          v-if="voucherApplied || paymentMethodPending || isDowngradeToPAYG"
          slot="row">
          <VMessage
            v-if="voucherApplied"
            type="success"
            :fullwidth="true">
            <template v-slot:icon>
              <InfoCircleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.tempVouchers.voucherAppliedMain') }}
            </template>
          </VMessage>
          <VMessage
            v-if="paymentMethodPending"
            type="warning"
            subtype="outlined"
            :fullwidth="true">
            <template v-slot:icon>
              <InfoCircleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.confirmPlan.ddPendingMessage') }}
            </template>
          </VMessage>
          <div v-if="isDowngradeToPAYG">
            <p>{{ $t('companySettings.billing.confirmPlan.confirmMessage', { name: selectedPlan.nickname }) }}</p>
            <p v-html="$t('companySettings.billing.cancelReasonLine')"></p>
            <VDropdown
              v-model="cancelReason"
              :list="cancelReasonOptions"
              :defaultTitle="$t('companySettings.billing.cancellationReasonDropdownTitle')"
              :fluid="true"
              :maxHeight="220"
              data-hj-suppress
              data-ec="billing_select_plan_modal"
              data-et="cancel_reason_dropdown" />
            <p class="comments-spacing">
              <strong>{{ $t('companySettings.billing.cancelReasonAdditionalComments') }}</strong>
            </p>
            <VInput
              type="textarea"
              :min-height="80"
              v-model="cancelReasonComments" />
          </div>
        </div>
        <div slot="row" v-if="isDowngrade">
          <VMessage
            type="info"
            subtype="outlined"
            :fullwidth="true">
            <template v-slot:icon>
              <InfoCircleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.confirmPlan.downgradeMessage') }}
            </template>
          </VMessage>
        </div>
        <div slot="row">
          <p v-if="selectedPlan">
            <span v-if="!isDowngradeToPAYG">{{ $t('companySettings.billing.confirmPlan.confirmMessage', { name: selectedPlan.nickname }) }}</span>
            <span v-if="selectedPlan.price && voucherApplied && selectedPlan.interval === 'month'">{{ $t('companySettings.billing.tempVouchers.confirmPriceMonthly', { price, interval: selectedPlan.interval }) }}</span>
            <span v-else-if="selectedPlan.price && voucherApplied && selectedPlan.interval === 'year'">{{ $t('companySettings.billing.tempVouchers.confirmPriceAnnually', { initialPrice, price, interval: selectedPlan.interval }) }}</span>
            <span v-else-if="selectedPlan.price">{{ $t('companySettings.billing.confirmPlan.confirmPrice', { price, interval: selectedPlan.interval }) }}</span>
          </p>
        </div>
        <div slot="row" v-if="paymentMethodAvailable === false">
          <VMessage
            v-if="!paymentMethodAvailable || paymentMethodPending"
            type="warning"
            :fullwidth="true">
            <template v-slot:icon>
              <ExclamationTriangleIcon />
            </template>
            <template v-slot:text>
              {{ $t('companySettings.billing.confirmPlan.noValidMethodsMessage') }}
            </template>
          </VMessage>
        </div>
        <div slot="formButtons">
          <VButton
            type="success"
            class="float-right"
            subtype="submit"
            :disabled="!canSwitchPlan"
            data-ec="billing_select_plan_modal"
            data-et="switch_button">
            <CheckIcon />
            <span>{{ $t('companySettings.billing.switch') }}</span>
          </VButton>
        </div>
      </VForm>
    </template>
  </VModal>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VModal from '@/components/deprecated/organisms/Modal.vue';
import VDropdown from '@/components/deprecated/molecules/Dropdown.vue';
import external from '@/utils/external';
import { CheckIcon, ExclamationTriangleIcon, InfoCircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'SelectPlanModal',
  inject: {
    $validator: '$validator',
  },
  components: {
    VButton,
    VMessage,
    VForm,
    VModal,
    VDropdown,
    VInput,
    CheckIcon,
    InfoCircleIcon,
    ExclamationTriangleIcon,
  },
  props: {
    currentPlan: {
      type: [Object],
      required: false,
    },
    selectedPlan: {
      type: [Object],
      required: false,
    },
    paymentMethodAvailable: {
      type: [Boolean],
      default: true,
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      cancelReasonOptions: [
        { title: this.$t('companySettings.billing.cancellationReasons.payG') },
        { title: this.$t('companySettings.billing.cancellationReasons.notSatisfied') },
        { title: this.$t('companySettings.billing.cancellationReasons.noLongerRequired') },
        { title: this.$t('companySettings.billing.cancellationReasons.differentSupplier') },
        { title: this.$t('companySettings.billing.cancellationReasons.other') },
      ],
      cancelReason: null,
      cancelReasonComments: null,
    };
  },
  computed: {
    ...mapGetters({
      paymentMethods: 'paymentMethods/results',
    }),
    voucherApplied() {
      return this.$route.query.voucher === process.env.VUE_APP_TEMP_VOUCHER
        && process.env.VUE_APP_TEMP_VOUCHER_ENABLED === 'true'
        && (this.currentPlan.id === 'free-trial' || this.currentPlan.id === 'pay-as-you-go');
    },
    price() {
      return this.$n(parseFloat(this.selectedPlan.price / 100), 'currency', this.selectedPlan.currency);
    },
    initialPrice() {
      return this.$n((parseFloat(this.selectedPlan.price * 0.9) / 100), 'currency', this.selectedPlan.currency);
    },
    isDowngrade() {
      return this.selectedPlan && this.currentPlan && this.selectedPlan.price < this.currentPlan.price;
    },
    isDowngradeToPAYG() {
      return this.isDowngrade && this.selectedPlan.id === 'pay-as-you-go';
    },
    canSwitchPlan() {
      if (
        this.isDowngradeToPAYG && (
          !this.cancelReason ||
          (!this.cancelReasonComments || this.cancelReasonComments.trim().length === 0)
        )
      ) {
        return false;
      }
      return !(!this.paymentMethodAvailable || this.paymentMethodPending);
    },
    paymentMethodPending() {
      if (this.paymentMethods && this.paymentMethods.length) {
        const defaultMethod = this.paymentMethods.find(method => method.is_default === true);
        if (defaultMethod.status === 'pending') return true;
      }

      return false;
    },
  },
  methods: {
    async switchPlan() {
      if (!this.canSwitchPlan) {
        return;
      }
      if (this.paymentMethodAvailable !== false && !this.paymentMethodPending) {
        this.loading = true;
        const data = {
          id: this.selectedPlan.id,
          tempVoucher: this.voucherApplied ? this.$route.query.voucher : null,
        };
        if (this.isDowngradeToPAYG) {
          data.cancelReason = this.cancelReason.title;
          data.cancelReasonComments = this.cancelReasonComments ? this.cancelReasonComments : '';
        }
        const { switchResult, switchError } = await this.$store.dispatch('plans/SET_CURRENT_PLAN', data);

        this.$router.replace({ query: null });

        if (switchResult) {
          this.gtmPurchaseEvent(this.selectedPlan.id);
          await this.$store.dispatch('plans/GET_CURRENT_PLAN');
          await this.$store.dispatch('metering/GET_USAGE');
          this.closeModal();
          return;
        }

        if (switchError && switchError.response.status === 402 && 'clientSecrets' in switchError.response.data) {
          const stripe = Stripe(process.env.VUE_APP_STRIPE_TOKEN); // eslint-disable-line
          await Promise.all(switchError.response.data.clientSecrets.map(async (secret) => {
            const { error } = await stripe.handleCardPayment(secret);
            if (error) {
              this.$bus.$emit('renderGlobalMessage', ({ message: error.message, icon: 'times', type: 'error' }));
              this.closeModal();
              return error;
            }

            return true;
          }));

          this.gtmPurchaseEvent(this.selectedPlan.id);

          await this.$store.dispatch('plans/GET_CURRENT_PLAN');
          await this.$store.dispatch('metering/GET_USAGE');
          this.closeModal();
          return;
        }

        if (switchError && switchError.response.status === 402 && !('clientSecrets' in switchError.response.data)) {
          this.$bus.$emit('renderGlobalMessage', ({ message: switchError.message, icon: 'times', type: 'error' }));
          this.closeModal();
          return;
        }

        if (switchError) {
          this.$bus.$emit('renderGlobalMessage', ({ message: switchError.message, icon: 'times', type: 'error' }));
          this.closeModal();
        }
      }
    },
    closeModal() {
      this.loading = false;
      this.$bus.$emit('closeModal');
    },
    gtmPurchaseEvent(plan) {
      external.trackEvent('planSwitch', {
        plan,
      });
    },
  },
};
</script>

<style scoped>
.comments-spacing {
  margin-top: var(--spacing-base);
}
</style>
